import api from '@/services/api';
import {
  companyDb,
  ensureDeviceId,
  getCompanyCode,
  setMeta,
} from './companyContext';
import { stockKey, type CatalogCustomer, type CatalogItem, type CatalogSupplier, type PosSyncMode } from './db';
import { useSyncStore } from '@/store/syncStore';
import { refreshQueueCounts } from './queue';

export interface BootstrapResult {
  itemCount: number;
  customerCount: number;
  supplierCount: number;
  bootstrappedAt: string;
}

export async function fetchAndApplyBootstrap(opts?: {
  limitItems?: number;
  limitCustomers?: number;
  limitSuppliers?: number;
}): Promise<BootstrapResult> {
  await ensureDeviceId();
  const res = await api.get('/method/saas_pos.api.offline_store.get_offline_bootstrap', {
    params: {
      limit_items: opts?.limitItems ?? 2000,
      limit_customers: opts?.limitCustomers ?? 2000,
      limit_suppliers: opts?.limitSuppliers ?? 500,
    },
  });
  const data = res.data.message;

  const db = companyDb();
  const items = (data.items || []) as CatalogItem[];
  const customers = (data.customers || []) as CatalogCustomer[];
  const suppliers = (data.suppliers || []) as CatalogSupplier[];

  await db.transaction('rw', [
    db.catalog_items,
    db.catalog_customers,
    db.catalog_suppliers,
    db.catalog_settings,
    db.local_stock,
    db.meta,
  ], async () => {
      await db.catalog_items.clear();
      await db.catalog_customers.clear();
      await db.catalog_suppliers.clear();
      await db.local_stock.clear();

      if (items.length) await db.catalog_items.bulkPut(items);
      if (customers.length) await db.catalog_customers.bulkPut(customers);
      if (suppliers.length) await db.catalog_suppliers.bulkPut(suppliers);

      for (const item of items) {
        if (item.item_type === 'Product') {
          await db.local_stock.put({
            id: stockKey(item.name),
            item: item.name,
            qty: Number(item.stock_quantity) || 0,
          });
          const variants = (item.variants || []) as Array<{ name: string; stock_quantity?: number }>;
          for (const v of variants) {
            await db.local_stock.put({
              id: stockKey(item.name, v.name),
              item: item.name,
              variant: v.name,
              qty: Number(v.stock_quantity) || 0,
            });
          }
        }
      }

      await db.catalog_settings.put({ key: 'settings', value: data.settings });
      await db.catalog_settings.put({ key: 'company', value: data.company });
      await db.catalog_settings.put({ key: 'categories', value: data.categories || [] });
      await db.catalog_settings.put({ key: 'uoms', value: data.uoms || [] });
      await db.catalog_settings.put({ key: 'cash_pockets', value: data.cash_pockets || [] });
      await db.catalog_settings.put({ key: 'bank_accounts', value: data.bank_accounts || [] });
      await db.catalog_settings.put({ key: 'promotions', value: data.promotions || [] });

      const syncedAt = data.bootstrapped_at || new Date().toISOString();
      await setMeta('catalogSyncedAt', syncedAt);
      await setMeta('companyCode', getCompanyCode());

      const mode = (data.settings?.pos_sync_mode || 'Live Online') as PosSyncMode;
      useSyncStore.getState().setPosSyncMode(mode);
      useSyncStore.getState().setCatalogSyncedAt(syncedAt);
  });

  await refreshQueueCounts();

  return {
    itemCount: items.length,
    customerCount: customers.length,
    supplierCount: suppliers.length,
    bootstrappedAt: data.bootstrapped_at,
  };
}

export async function loadSyncModeFromSettings(): Promise<PosSyncMode> {
  const row = await companyDb().catalog_settings.get('settings');
  const settings = row?.value as { pos_sync_mode?: PosSyncMode } | undefined;
  if (settings?.pos_sync_mode) {
    useSyncStore.getState().setPosSyncMode(settings.pos_sync_mode);
    return useSyncStore.getState().posSyncMode;
  }
  try {
    const res = await api.get('/method/saas_pos.api.offline_store.get_pos_sync_status');
    const mode = (res.data.message?.pos_sync_mode || 'Live Online') as PosSyncMode;
    useSyncStore.getState().setPosSyncMode(mode);
    return useSyncStore.getState().posSyncMode;
  } catch {
    return useSyncStore.getState().posSyncMode;
  }
}

export async function getCachedSettings<T = Record<string, unknown>>(): Promise<T | null> {
  const row = await companyDb().catalog_settings.get('settings');
  return (row?.value as T) || null;
}
